/* eslint-disable no-nested-ternary */
import React from "react";
import { Col, Row, Spin, Tooltip } from "antd";
import queryString from "query-string";
import { useHistory } from "react-router-dom";
import {
  HeartOutlined,
  LoadingOutlined,
  CloseSquareOutlined,
} from "@ant-design/icons";
import get from "lodash/get";
import moment from "moment";
import ReactPlayer from "react-player";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import { pushReaction } from "../../../common-definitions/reaction";
import StatusLive from "../../../models/liveStream";
import { client, leaveChannel, joinChannel } from "../../../libs/agora";
import audio from "../../../assets/img/audio.svg";
import audioMute from "../../../assets/img/audioMute.svg";
import LiveStreamingChat from "../LiveStreamingChat";
import styles from "./LiveStreamVideo.module.scss";
import { Program } from "../../../API";
import {
  getDuration,
  getDurationTime,
} from "../../../common-definitions/moment-utils";
import { FormatType } from "../../../models/date";
import {
  CameraSetting,
  LiveStreamStatus,
  FeartureProduct,
} from "../../../common-definitions/common-enum";
import { isMobileOrTablet } from "../../../common-definitions/device";
import { useViewport } from "../../../hooks/useViewport";
import { getImage, getUploadVideo } from "../../../common-definitions/storage";
import Image from "../../atoms/Image";
import { ProgramData } from "../../../context/program";

interface LiveStreamVideoProps {
  createReaction: (isRehearsal: 0 | 1) => void;
  program: Program;
  handleChangeStatus: (status: StatusLive) => void;
  fetched: boolean;
  isVideoMode: boolean;
  programProductList: ProgramData[];
  onPlayVideoArchived: () => void;
}

const initialState = {
  statusLive: StatusLive.before,
  programImage: "",
  timerId: 0 as unknown as NodeJS.Timer,
  offMic: false,
  isOffline: false,
  isPlayed: false,
  expand: false,
  counter: 0,
  logoUrl: "",
  showProgramDetail: false,
  isOffAudio: true,
  isDisplayAudioMuteText: true,
  uploadedVideoUrl: "",
  isShowLineBtnAtPlayVideoMode: true,
};

const LiveStreamVideo: React.FC<LiveStreamVideoProps> = ({
  program,
  createReaction,
  handleChangeStatus,
  fetched,
  isVideoMode,
  programProductList,
  onPlayVideoArchived,
}) => {
  const history = useHistory();
  const search =
    queryString.parse(history?.location?.search || "") || ({} as any); // eslint-disable-line
  const viewportWidth = useViewport();
  const isMobile = viewportWidth <= 1024 || isMobileOrTablet();
  const [state, setState] = React.useState(initialState);
  const [isLoading, setLoading] = React.useState(true);
  const [initialized, setInitialized] = React.useState(false);
  const timeDurationRef = React.useRef<HTMLParagraphElement | null>(null);
  const ref = React.useRef<HTMLDivElement | null>(null);
  const refThumbnailImage = React.useRef<HTMLDivElement | null>(null);
  const refPlayer = React.useRef<ReactPlayer>(null);
  const startTimer = (liveStreamStartTime: string) => {
    const timerId = setInterval(() => {
      const timeNow = moment();
      const startTime = moment(liveStreamStartTime);
      if (timeDurationRef.current?.innerHTML) {
        timeDurationRef.current.innerHTML = getDurationTime(startTime, timeNow);
      }
    }, 1000);
    setState((s) => {
      return { ...s, timerId, rehesalStartTime: liveStreamStartTime };
    });
  };
  const stopTimer = React.useCallback(() => {
    clearInterval(state.timerId);
  }, [state.timerId]);

  React.useEffect(() => {
    if (ref.current) {
      ref.current.style.maxWidth = "none";
      ref.current.style.maxHeight = "none";
    }
    if (refThumbnailImage.current) {
      refThumbnailImage.current.style.maxWidth = "none";
      refThumbnailImage.current.style.maxHeight = "none";
    }
    if (ref.current && program.cameraSetting === CameraSetting.horizontal) {
      const horizontalRatio = 640 / 360;
      if (
        ref.current.clientWidth / ref.current.clientHeight <
        horizontalRatio
      ) {
        ref.current.style.maxWidth = "none";
        ref.current.style.maxHeight = `${
          ref.current.clientWidth / horizontalRatio
        }px`;
        if (refThumbnailImage.current) {
          refThumbnailImage.current.style.maxWidth = "none";
          refThumbnailImage.current.style.maxHeight = `${
            ref.current.clientWidth / horizontalRatio
          }px`;
        }
      } else {
        ref.current.style.maxHeight = "none";
        ref.current.style.maxWidth = `${
          ref.current.clientHeight * horizontalRatio
        }px`;
        if (refThumbnailImage.current) {
          refThumbnailImage.current.style.maxHeight = "none";
          refThumbnailImage.current.style.maxWidth = `${
            ref.current.clientHeight * horizontalRatio
          }px`;
        }
      }
    }
  }, [
    viewportWidth,
    ref.current,
    program.cameraSetting,
    refThumbnailImage.current,
  ]);

  React.useEffect(() => {
    switch (program.livestreamStatus) {
      case LiveStreamStatus.BEFORE:
        if (!state.isPlayed) {
          stopTimer();
          setState(initialState);
        }
        setLoading(false);
        break;
      case LiveStreamStatus.ONLIVE:
        if (program.livestreamStartDateTime) {
          stopTimer();
          startTimer(program.livestreamStartDateTime);
          if (state.statusLive === StatusLive.before) {
            setState((s) => {
              return {
                ...s,
                statusLive: StatusLive.live,
              };
            });
          }
        }
        break;
      case LiveStreamStatus.END:
        setState((s) => {
          return {
            ...s,
            statusLive: StatusLive.after,
            isPlayed: false,
          };
        });
        setLoading(false);
        break;
      default:
        break;
    }
  }, [program]);

  React.useEffect(() => {
    if (state.statusLive === StatusLive.live && state.isPlayed) {
      if (
        program.livestreamStartDateTime &&
        moment(program.livestreamStartDateTime).unix() <= moment().unix()
      ) {
        stopTimer();
        startTimer(program.livestreamStartDateTime);
      } else {
        stopTimer();
        startTimer(moment().format(FormatType["YYYY/MM/DD HH:mm:ss"]));
      }
    }
    if (state.statusLive === StatusLive.after) {
      stopTimer();
      leaveChannel();
    }
  }, [state.statusLive, state.isPlayed]);

  function subcribeEvent() {
    client.on("user-info-updated", async (uid, msg) => {
      switch (msg) {
        case "mute-audio":
          setState((s) => {
            return {
              ...s,
              offMic: true,
            };
          });
          break;
        case "unmute-audio":
          setState((s) => {
            return {
              ...s,
              offMic: false,
            };
          });
          break;
        case "mute-video":
          setState((s) => {
            return {
              ...s,
              statusLive: StatusLive.offCam,
            };
          });
          break;
        case "unmute-video":
          setState((s) => {
            return {
              ...s,
              statusLive: StatusLive.live,
            };
          });
          break;
        default:
          break;
      }
    });
  }

  async function subcribeExistingUser() {
    if (client.remoteUsers.length > 0) {
      const host = client.remoteUsers[0];
      if (host.hasVideo && ref.current) {
        await client.subscribe(host, "video");
        host.videoTrack?.play(ref.current as HTMLElement);
        subcribeEvent();
        setState((s) => {
          return {
            ...s,
            statusLive: StatusLive.live,
            isPlayed: true,
          };
        });
      }
      if (host.hasAudio) {
        await client.subscribe(host, "audio");
      }
    }
  }

  React.useEffect(() => {
    if (!state.isOffline) {
      subcribeExistingUser();
    }
  }, [state.isOffline]);

  const initView = async () => {
    if (program.livestreamStatus === LiveStreamStatus.ONLIVE) {
      setState((s) => {
        return {
          ...s,
          statusLive: StatusLive.offCam,
          offMic: true,
          isPlayed: true,
        };
      });
    }
    await joinChannel(program.id);
    subcribeExistingUser();
    client.on("user-published", async (user, mediaType) => {
      await client.subscribe(user, mediaType);
      if (user.hasVideo && ref.current) {
        await client.subscribe(user, "video");
        user.videoTrack?.play(ref.current as HTMLElement);
        subcribeEvent();
        setState((s) => {
          return {
            ...s,
            statusLive: StatusLive.live,
            isPlayed: true,
          };
        });
      }
      if (user.hasAudio) {
        await client.subscribe(user, "audio");
      }
    });

    client.on("user-left", async (_, reason) => {
      if (reason === "Quit") {
        if (program.livestreamStatus === LiveStreamStatus.ONLIVE) {
          setState((s) => {
            return {
              ...s,
              isPlayed: true,
              statusLive: StatusLive.offCam,
              offMic: true,
            };
          });
        } else {
          setState((s) => {
            return {
              ...s,
              statusLive: StatusLive.before,
              isPlayed: false,
            };
          });
          setLoading(false);
        }
      }
      if (reason === "ServerTimeOut") {
        setState((s) => {
          return {
            ...s,
            isOffline: true,
            isPlayed: false,
          };
        });
      }
    });

    client.on("network-quality", (stats) => {
      setState((s) => {
        return {
          ...s,
          isOffline: stats.downlinkNetworkQuality >= 4,
        };
      });
    });

    setTimeout(() => {
      setLoading(false);
    }, 2000);
  };

  const handleToggleAudio = async () => {
    const host = client.remoteUsers[0];
    if (host?.hasAudio) {
      await client.subscribe(host, "audio");
      if (state.isOffAudio) {
        host?.audioTrack?.stop();
      } else {
        host?.audioTrack?.play();
      }
    }
  };

  React.useEffect(() => {
    handleToggleAudio();
  }, [state.isOffAudio, state.statusLive, state.offMic]);

  const getLogoImage = async (logoUrl: string) => {
    const image = (await getImage(logoUrl, "customer")) as string;
    setState((s) => {
      return {
        ...s,
        logoUrl: image || "",
      };
    });
  };
  const getProgramImage = async () => {
    const image = (await getImage(
      program.thumbnailUrl || "",
      "program"
    )) as string;
    setState((s) => {
      return {
        ...s,
        programImage: image || "",
      };
    });
  };

  const getProgramUploadVideo = async () => {
    const video = (await getUploadVideo(
      program.uploadVideoUrl || "",
      "upload"
    )) as string;
    setState((s) => {
      return {
        ...s,
        uploadedVideoUrl: video || "",
      };
    });
  };

  React.useEffect(() => {
    if (program.thumbnailUrl) {
      getProgramImage();
    }
    const logoUrl = get(program.customer, "items[0].logoUrl", "");
    if (logoUrl && logoUrl.length > 0) {
      getLogoImage(logoUrl);
    } else if (state.logoUrl) {
      setState((s) => {
        return {
          ...s,
          logoUrl: "",
        };
      });
    }
  }, [program.thumbnailUrl, program.customer]);

  React.useEffect(() => {
    handleChangeStatus(state.statusLive);
  }, [state.statusLive]);

  const stop = async () => {
    await leaveChannel();
  };
  React.useEffect(() => {
    const allowRehearsal =
      search.liveStatus && search.liveStatus === "isRehearsal";
    if (
      ((program.livestreamStatus === LiveStreamStatus.BEFORE &&
        allowRehearsal) ||
        program.livestreamStatus === LiveStreamStatus.ONLIVE) &&
      !initialized
    ) {
      if (!state.isPlayed) {
        stop();
        setInitialized(true);
        initView();
      }
    }
  }, [program, initialized, state.isPlayed]);

  React.useEffect(() => {
    return () => {
      stop();
    };
  }, []);

  const addReactions = (isHighlight: boolean) => {
    pushReaction(isHighlight);
    const isRehearsal =
      state.statusLive !== StatusLive.before &&
      program.livestreamStatus === LiveStreamStatus.BEFORE
        ? 1
        : 0;
    createReaction(isRehearsal);
  };

  const JPLineAddFrdBtn = program?.lineAddFriendBtnHtml?.replace(
    "en.png",
    "ja.png"
  );

  const lineAddFrdBtn = React.createElement("div", {
    dangerouslySetInnerHTML: {
      __html: JPLineAddFrdBtn?.replace(/href/g, "target='_blank' href") || null,
    },
  });

  const renderMsg = React.useMemo(() => {
    let top = "";
    let bottom = "";
    if (
      state.statusLive === StatusLive.offCam &&
      !state.offMic &&
      state.isPlayed
    ) {
      top = "動画を一時中断してます。";
      bottom = "再開まで少々お待ちください。";
    }
    if (
      state.statusLive === StatusLive.offCam &&
      state.offMic &&
      state.isPlayed
    ) {
      top = "配信が一時中断されました。";
      bottom = "再開まで少々お待ちください。";
    }
    if (state.statusLive === StatusLive.after && !state.isPlayed) {
      top = "配信は終了しました";
      bottom = "ご視聴ありがとうございました";
    }
    if (top === "" && bottom === "") {
      return null;
    }
    return (
      <div className={styles.endLiveText}>
        <p className={styles.textTop}>{top}</p>
        <p
          className={`${
            program?.lineAddFriendBtnHtml ? styles.textTop : styles.texBottom
          }`}
        >
          {bottom}
        </p>
        <p
          className={`${
            program?.lineAddFriendBtnHtml ? styles.lastText : styles.none
          }`}
        >
          LINE友だち追加で限定クーポン GET！
        </p>
        {lineAddFrdBtn}
      </div>
    );
  }, [state.statusLive, state.offMic]);

  function handleClickFeaturedProduct(dataType: string) {
    const productDisplay = document.getElementById(dataType);
    if (productDisplay) {
      productDisplay.scrollIntoView({ behavior: "smooth" });
    }
  }

  const isLivePlay =
    program.livestreamStatus === LiveStreamStatus.ONLIVE ||
    (state.statusLive === StatusLive.live && state.isPlayed);

  const pinProduct = programProductList.filter(
    (prg: ProgramData) => prg.isFeatured === FeartureProduct.IS_FEARTURE
  );
  React.useEffect(() => {
    if (program?.uploadVideoUrl) {
      getProgramUploadVideo();
    }
  }, [program.uploadVideoUrl]);

  return (
    <div className={styles.inner}>
      {state.statusLive !== StatusLive.after &&
        program.lineAddFriendBtnHtml &&
        isMobile && (
          <div className={`${styles.lineArea} ${styles.centerContent}`}>
            <div className={styles.lineDesc}>
              友だち追加でライブ配信をお知らせ！
            </div>
            {lineAddFrdBtn}
          </div>
        )}
      <div
        className={`${styles.videoLiveStream} ${
          isLivePlay ? "has-reaction" : ""
        } ${
          program.cameraSetting === CameraSetting.vertical
            ? "vertical"
            : "horizontal"
        }`}
      >
        {state.statusLive !== StatusLive.after &&
          program.lineAddFriendBtnHtml &&
          !isMobile && (
            <div className={`${styles.lineArea} ${styles.centerContent}`}>
              <div className={styles.lineDesc}>
                友だち追加でライブ配信をお知らせ！
              </div>
              {lineAddFrdBtn}
            </div>
          )}
        <div
          className={`${styles.videoBackground} ${
            state.isOffline && isLivePlay ? styles.errorBackground : ""
          } ${
            program.lineAddFriendBtnHtml && !isMobile
              ? styles.resizeVideoBg
              : null
          }`}
        >
          {isMobile ? (
            <Row className={styles.headerSP}>
              <Col flex="64px">
                <Image defaultClass={styles.logo} src={state.logoUrl} />
              </Col>
              <Col className={styles.customerInfo}>
                <div className={styles.textNote}>{program.programName}</div>
                <Row className={styles.timeLive}>
                  {state.statusLive === StatusLive.offCam &&
                    state.offMic &&
                    state.isPlayed && (
                      <p className={styles.beforeLiveText}>一時停止</p>
                    )}
                  {!state.isPlayed && state.statusLive === StatusLive.live && (
                    <p className={styles.beforeLiveText}>一時停止</p>
                  )}
                  {state.statusLive === StatusLive.after && !state.isPlayed && (
                    <p className={styles.beforeLiveText}>
                      {program.uploadVideoUrl ? "アーカイブ動画" : "配信終了"}
                    </p>
                  )}
                  {state.statusLive === StatusLive.before &&
                    !state.isPlayed && (
                      <p className={styles.beforeLiveText}>配信前</p>
                    )}
                  {state.isPlayed && state.statusLive === StatusLive.live && (
                    <p className={styles.statusLive}>LIVE</p>
                  )}
                  {state.statusLive !== StatusLive.before &&
                    state.statusLive !== StatusLive.after && (
                      <p ref={timeDurationRef}>00:00</p>
                    )}
                  {state.statusLive === StatusLive.live ||
                  (state.statusLive !== StatusLive.offCam &&
                    program.livestreamStatus === LiveStreamStatus.ONLIVE) ? (
                    <>
                      {state.isOffAudio ? (
                        <div
                          className={styles.audio}
                          onClick={() => {
                            setState((s) => ({
                              ...s,
                              isOffAudio: false,
                              isDisplayAudioMuteText: false,
                            }));
                          }}
                        >
                          <img
                            src={audioMute}
                            alt="audioMute"
                            className={styles.audioImg}
                          />
                        </div>
                      ) : (
                        <div
                          className={styles.audio}
                          onClick={() => {
                            setState((s) => ({
                              ...s,
                              isOffAudio: true,
                            }));
                          }}
                        >
                          <img
                            src={audio}
                            alt="audio"
                            className={styles.audioImg}
                          />
                        </div>
                      )}
                    </>
                  ) : null}
                </Row>
              </Col>
            </Row>
          ) : null}
          <div
            className={
              program.cameraSetting === CameraSetting.vertical
                ? styles.thumnailProgram
                : isMobile
                ? styles.thumnailHorizontalSP
                : styles.thumnailProgramFullWidth
            }
          >
            <div ref={ref} className={styles.imageProgram} />
            {state.statusLive === StatusLive.live && (
              <div id="product-active" />
            )}
            <TransitionGroup
              className={styles.featuredProduct}
              id="featured-products"
            >
              {pinProduct.map((prg: ProgramData) => (
                <CSSTransition key={prg.dataType} timeout={500}>
                  <div
                    className="featured-product"
                    // eslint-disable-next-line
                    onClick={() => handleClickFeaturedProduct(prg.dataType)}
                  >
                    <div className={styles.productImage}>
                      {prg?.product?.productImageUrl &&
                      prg?.product?.productName ? (
                        <Image
                          src={prg.product?.productImageUrl || ""}
                          alt={prg.product?.productName || ""}
                          isSquare
                        />
                      ) : null}
                    </div>
                    <div className={styles.productName}>を紹介しています</div>
                  </div>
                </CSSTransition>
              ))}
            </TransitionGroup>
          </div>
          {state.statusLive !== StatusLive.live &&
            state.statusLive !== StatusLive.after && (
              <div
                className={
                  program.cameraSetting === CameraSetting.vertical
                    ? styles.thumnailProgram
                    : styles.thumnailProgramFullWidth
                }
              >
                <div id="product-active" />
                <div className={styles.loadingView}>
                  <div className={styles.loadingViewChild}>
                    {state.programImage.length > 0 ? (
                      <div
                        className={styles.thumbnailImage}
                        ref={refThumbnailImage}
                      >
                        <Image
                          src={state.programImage}
                          defaultClass={
                            isMobile
                              ? styles.thumnailImgSP
                              : styles.thumnailImgPC
                          }
                        />
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            )}
          {state.statusLive === StatusLive.after && (
            <div
              className={
                program.cameraSetting === CameraSetting.vertical
                  ? styles.thumnailProgram
                  : styles.thumnailProgramFullWidth
              }
            >
              <div id="product-active" />
              <div
                className={`${styles.loadingView} ${
                  isMobile ? styles.resizeVideo : styles.originalVideo
                }`}
              >
                {program.uploadVideoUrl ? (
                  <ReactPlayer
                    light={state.programImage || ""}
                    ref={refPlayer}
                    controls
                    playsinline
                    width="100%"
                    height="100%"
                    url={state.uploadedVideoUrl}
                    onPlay={() => {
                      if (refPlayer?.current?.getCurrentTime() === 0) {
                        onPlayVideoArchived();
                      }
                    }}
                  />
                ) : null}
                {state.statusLive === StatusLive.after &&
                  program.uploadVideoUrl &&
                  program.lineAddFriendBtnHtml &&
                  state.isShowLineBtnAtPlayVideoMode && (
                    <div
                      className={`${styles.lineArea} ${styles.lineAtPlayVideoMode}`}
                    >
                      {lineAddFrdBtn}
                      <div
                        className={`${styles.lineDesc} ${styles.reFontSize}`}
                      >
                        お得な情報を先行してお届け！
                      </div>
                      <CloseSquareOutlined
                        className={styles.lineCloseBtn}
                        style={{ fontSize: "14px" }}
                        onClick={() => {
                          setState((s) => ({
                            ...s,
                            isShowLineBtnAtPlayVideoMode: false,
                          }));
                        }}
                      />
                    </div>
                  )}
                {state.programImage.length > 0 && !program.uploadVideoUrl ? (
                  <div
                    className={styles.thumbnailImage}
                    ref={refThumbnailImage}
                  >
                    <Image
                      src={state.programImage}
                      defaultClass={
                        isMobile ? styles.thumnailImgSP : styles.thumnailImgPC
                      }
                    />
                  </div>
                ) : null}
              </div>
            </div>
          )}
          {state.statusLive === StatusLive.live ||
          (state.statusLive !== StatusLive.offCam &&
            program.livestreamStatus === LiveStreamStatus.ONLIVE) ? (
            <>
              {state.isDisplayAudioMuteText && state.isOffAudio && (
                <div
                  className={styles.audioText}
                  onClick={() => {
                    setState((s) => ({
                      ...s,
                      isOffAudio: false,
                      isDisplayAudioMuteText: false,
                    }));
                  }}
                >
                  <div>
                    <img
                      src={audioMute}
                      alt="audioMute"
                      className={styles.audioMuteImgLarge}
                    />
                  </div>
                  <span className={styles.audioMuteText}>ミュートを解除</span>
                </div>
              )}
            </>
          ) : null}
          {((state.isOffline && isLivePlay) ||
            (!state.isPlayed && state.statusLive === StatusLive.live)) && (
            <div className={styles.thumnailProgram}>
              <div id="product-active" />
              <div className={styles.loadingView}>
                <div className={styles.loadingViewChild}>
                  <Spin
                    indicator={
                      <LoadingOutlined
                        style={{ fontSize: 60, color: "white" }}
                        spin
                      />
                    }
                  />
                  <span className={styles.text}>ネットワークエラー</span>
                  <span className={styles.text}>少々お待ちください</span>
                </div>
              </div>
            </div>
          )}
        </div>
        {!(state.statusLive === StatusLive.after && program.uploadVideoUrl) &&
          renderMsg}
        {!isMobile && (
          <div className={styles.textLive}>
            {state.statusLive === StatusLive.offCam &&
              state.offMic &&
              state.isPlayed && <p className={styles.offLiveText}>一時停止</p>}
            {!state.isPlayed && state.statusLive === StatusLive.live && (
              <p className={styles.offLiveText}>一時停止</p>
            )}
            {state.statusLive === StatusLive.after && !state.isPlayed && (
              <p className={styles.offLiveText}>
                {program.uploadVideoUrl ? "アーカイブ動画" : "配信終了"}
              </p>
            )}
            {state.statusLive === StatusLive.before && !state.isPlayed && (
              <p className={styles.offLiveText}>配信前</p>
            )}
            {state.isPlayed && state.statusLive === StatusLive.live && (
              <p className={styles.status}>LIVE</p>
            )}
            {state.statusLive !== StatusLive.before &&
              state.statusLive !== StatusLive.after && (
                <p ref={timeDurationRef}>00:00</p>
              )}
            {state.statusLive === StatusLive.live ||
            (state.statusLive !== StatusLive.offCam &&
              program.livestreamStatus === LiveStreamStatus.ONLIVE) ? (
              <>
                {state.isOffAudio ? (
                  <div
                    className={`${styles.audio} ${styles.audioPC}`}
                    onClick={() => {
                      setState((s) => ({
                        ...s,
                        isOffAudio: false,
                        isDisplayAudioMuteText: false,
                      }));
                    }}
                  >
                    <img
                      src={audioMute}
                      alt="audioMute"
                      className={styles.audioImg}
                    />
                  </div>
                ) : (
                  <div
                    className={`${styles.audio} ${styles.audioPC}`}
                    onClick={() => {
                      setState((s) => ({
                        ...s,
                        isOffAudio: true,
                      }));
                    }}
                  >
                    <img src={audio} alt="audio" className={styles.audioImg} />
                  </div>
                )}
              </>
            ) : null}
            <Tooltip
              placement="top"
              title={program.programName}
              overlayClassName={styles.toolTip}
            >
              <p className={styles.textNote}>{program.programName}</p>
            </Tooltip>
          </div>
        )}
        {state.statusLive !== StatusLive.before && isMobile && !isVideoMode && (
          <LiveStreamingChat
            program={program}
            statusLive={state.statusLive}
            isOverlay
            fetched={fetched}
          />
        )}
        {isLivePlay && (
          <div className={styles.reactionWrapper}>
            <button
              className={styles.reactionBtn}
              type="button"
              onClick={() => addReactions(true)}
            >
              <HeartOutlined />
            </button>
            <div id="reactions" />
          </div>
        )}
        {isLoading && (
          <div className={styles.initView}>
            <div className={styles.loadingView}>
              <div className={styles.loadingViewChild}>
                <Spin
                  indicator={
                    <LoadingOutlined
                      style={{ fontSize: 60, color: "white" }}
                      spin
                    />
                  }
                />
              </div>
            </div>
          </div>
        )}
      </div>
      <div className={styles.note}>
        動画が表示されない方は一度タブを閉じて再度アクセスしてください
      </div>
      {!isMobile ? (
        <div className={styles.content}>
          <div className={styles.logo}>
            <Image defaultClass={styles.logo} src={state.logoUrl} />
          </div>
          <div className={styles.intro}>
            {program.programDescription && (
              <p
                className={styles.textClose}
                onClick={() =>
                  setState({
                    ...state,
                    showProgramDetail: !state.showProgramDetail,
                  })
                }
                aria-hidden="true"
              >
                {state.showProgramDetail ? "閉じる" : "番組詳細を見る"}
              </p>
            )}
            <span>{get(program.customer, "items[0].companyName", "")}</span>
            <p className={styles.textContent}>
              {state.statusLive === StatusLive.live || state.isPlayed
                ? "配信日"
                : state.statusLive === StatusLive.after
                ? "配信日"
                : "配信予定日"}
              ：
              {getDuration(
                program.livestreamStartDateTime,
                program.livestreamEndDateTime
              )}
            </p>
            {state.showProgramDetail && (
              <p className={styles.textContent}>{program.programDescription}</p>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default LiveStreamVideo;
